import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_PHONE = '6598366186';
const DEFAULT_WEBSITE = 'https://njklogisticsservices.com';
const DEFAULT_BOOKING = 'https://njklogisticsservices.com/booking';
const DEFAULT_CARD = `${window.location.origin}/card`;
const DEFAULT_WA_MSG = "Hi NJK Logistics, I'd like to enquire about your services.";

export const QRCodes = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState(DEFAULT_PHONE);
  const [waMessage, setWaMessage] = useState(DEFAULT_WA_MSG);
  const [website, setWebsite] = useState(DEFAULT_WEBSITE);
  const [booking, setBooking] = useState(DEFAULT_BOOKING);
  const [cardUrl, setCardUrl] = useState(DEFAULT_CARD);
  const [size, setSize] = useState(300);
  const [driverName, setDriverName] = useState('');
  const [knownDrivers, setKnownDrivers] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/delivery-orders/drivers/names`)
      .then((res) => setKnownDrivers(res.data.drivers || []))
      .catch(() => {});
  }, []);

  const driverUrl = driverName.trim() ? `${window.location.origin}/driver/${encodeURIComponent(driverName.trim())}` : '';

  const waUrl = `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(waMessage)}`;

  const downloadQR = (canvasId, fileName) => {
    const canvas = document.querySelector(`#${canvasId} canvas`);
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
  };

  const printQR = (canvasId, title) => {
    const canvas = document.querySelector(`#${canvasId} canvas`);
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>${title}</title>
      <style>body{font-family:Arial;text-align:center;padding:40px;}
      h1{color:#1a1a2e;font-size:24px;margin-bottom:8px;}
      p{color:#666;font-size:14px;margin:4px 0;}
      img{margin:20px 0;}
      </style></head><body>
      <h1>NJK LOGISTICS PTE LTD</h1>
      <p>Swift, Secure and Reliable</p>
      <p>${title}</p>
      <img src="${url}" />
      <p>Tel: 98366186 | inquiry@njklogistics.com</p>
      </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const Card1 = ({ id, title, subtitle, value, file }) => (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </CardHeader>
      <CardContent className="text-center">
        <div id={id} className="inline-block p-4 bg-white border-2 border-gray-200 rounded-lg">
          <QRCodeCanvas value={value} size={size} level="H" includeMargin={true}
                        imageSettings={{
                          src: '/logo.png',
                          height: 40, width: 40, excavate: true,
                        }} />
        </div>
        <p className="text-xs text-gray-400 mt-2 break-all px-4">{value}</p>
        <div className="flex gap-2 justify-center mt-3">
          <Button size="sm" variant="outline" onClick={() => downloadQR(id, file)} data-testid={`download-${id}`}>
            <Download size={14} className="mr-1" /> Download PNG
          </Button>
          <Button size="sm" variant="outline" onClick={() => printQR(id, title)} data-testid={`print-${id}`}>
            <Printer size={14} className="mr-1" /> Print
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" onClick={() => navigate('/admin/billing')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">QR Codes</h1>
            <p className="text-gray-600">For business cards, vehicles, flyers — scan-to-WhatsApp & scan-to-book</p>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader><CardTitle>Customise</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>WhatsApp Number (with country code)</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="6598366186" data-testid="phone-input" />
            </div>
            <div>
              <Label>Website URL</Label>
              <Input value={website} onChange={(e) => setWebsite(e.target.value)} data-testid="website-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Booking Page URL</Label>
              <Input value={booking} onChange={(e) => setBooking(e.target.value)} data-testid="booking-input" />
            </div>
            <div className="md:col-span-2">
              <Label>WhatsApp Pre-filled Message</Label>
              <Textarea value={waMessage} onChange={(e) => setWaMessage(e.target.value)} rows={2} data-testid="wa-msg" />
            </div>
            <div>
              <Label>QR Code Size (px)</Label>
              <Input type="number" min="150" max="600" value={size}
                     onChange={(e) => setSize(parseInt(e.target.value) || 300)} data-testid="size-input" />
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="card" className="mb-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="card" data-testid="tab-card">💳 Smart Card</TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">📱 WhatsApp</TabsTrigger>
            <TabsTrigger value="booking" data-testid="tab-booking">📅 Booking</TabsTrigger>
            <TabsTrigger value="website" data-testid="tab-website">🌐 Website</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="tab-drivers">🚛 Drivers</TabsTrigger>
          </TabsList>
          <TabsContent value="card">
            <Card1 id="qr-card"
                   title="💳 Smart Business Card (Recommended)"
                   subtitle="Best for business cards — scan opens a branded mobile page with Save Contact, Call, WhatsApp, Email, Book Now & more"
                   value={cardUrl}
                   file="NJK_SmartCard_QR.png" />
            <div className="mt-3 px-4">
              <Label>Smart Card URL</Label>
              <Input value={cardUrl} onChange={(e) => setCardUrl(e.target.value)} data-testid="card-url-input" />
              <p className="text-xs text-gray-500 mt-1">Tip: open <a href="/card" target="_blank" rel="noreferrer" className="text-blue-600 underline">/card</a> to preview the landing page customers will see.</p>
            </div>
          </TabsContent>
          <TabsContent value="whatsapp">
            <Card1 id="qr-whatsapp"
                   title="📱 Scan to WhatsApp Chat"
                   subtitle="Direct-to-chat — customer scans → instant pre-filled WhatsApp opens"
                   value={waUrl}
                   file="NJK_WhatsApp_QR.png" />
          </TabsContent>
          <TabsContent value="booking">
            <Card1 id="qr-booking"
                   title="📅 Scan to Book Online"
                   subtitle="Best for flyers/vehicles — customer scans → goes straight to booking form"
                   value={booking}
                   file="NJK_Booking_QR.png" />
          </TabsContent>
          <TabsContent value="website">
            <Card1 id="qr-website"
                   title="🌐 Scan to Visit Website"
                   subtitle="Best for general marketing — customer scans → opens homepage"
                   value={website}
                   file="NJK_Website_QR.png" />
          </TabsContent>
          <TabsContent value="drivers">
            <Card className="mb-4">
              <CardHeader>
                <CardTitle>🚛 Driver Job Sheet QR</CardTitle>
                <p className="text-sm text-gray-500">Each driver scans their own QR to open today's job list with navigation & call buttons — no login needed.</p>
              </CardHeader>
              <CardContent>
                <Label>Driver Name (must match the "Driver Name" on Delivery Orders)</Label>
                <Input value={driverName} onChange={(e) => setDriverName(e.target.value)} placeholder="e.g. Ah Seng" data-testid="driver-name-input" />
                {knownDrivers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {knownDrivers.map((d) => (
                      <button
                        key={d}
                        onClick={() => setDriverName(d)}
                        className={`px-3 py-1 rounded-full text-sm border transition-colors ${driverName === d ? 'bg-[#5c1a1a] text-white border-[#5c1a1a]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#5c1a1a]'}`}
                        data-testid={`driver-chip-${d.replace(/\s+/g, '-')}`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            {driverUrl ? (
              <Card1 id="qr-driver"
                     title={`🚛 Job Sheet — ${driverName.trim()}`}
                     subtitle="Driver scans → sees today's jobs with Navigate, Call & Mark Delivered"
                     value={driverUrl}
                     file={`NJK_Driver_${driverName.trim().replace(/\s+/g, '_')}_QR.png`} />
            ) : (
              <Card><CardContent className="py-10 text-center text-gray-400" data-testid="driver-qr-placeholder">Type or pick a driver name above to generate their QR code.</CardContent></Card>
            )}
          </TabsContent>
        </Tabs>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-sm text-blue-900">
            <strong>💡 Tip:</strong> Print QR codes at <strong>2cm × 2cm minimum</strong> on business cards. For vehicle stickers, use <strong>10cm × 10cm</strong> or larger. Always test by scanning with your phone before printing in bulk!
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QRCodes;
